/**
 * web/src/hooks/queries/useNudgePreview.ts
 * Live preview of ONE planned nudge (GET /api/tracked-emails/:id/nudges/:index/preview)
 * — powers the review/send sheet: nothing sends without this render. The server
 * computes "as of now" (recipients, outstanding assets, body) and is strictly
 * read-only; the same compose runs again at actual send time. Fetched fresh on
 * every sheet open (staleTime 0, no retry on 409 — the entry may have just
 * fired or been cancelled underneath us).
 */
import { useQuery } from '@tanstack/react-query';
import { fetchNudgePreview } from '../../api/tracked-emails';
import { ApiClientError } from '../../lib/api';

/**
 * Fetches the live send-preview for one planned nudge entry. Disabled until
 * `enabled` (sheet open) and a tracked-email id are present.
 *
 * @param trackedEmailId the TrackedEmail carrying the plan (undefined = idle)
 * @param index zero-based entry position in the plan
 * @param enabled fetch only while the review sheet is open
 * @returns React Query result for NudgePreview
 * @sideEffects HTTP GET
 * @idempotent yes
 */
export function useNudgePreview(
  trackedEmailId: string | undefined,
  index: number,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['nudge-preview', trackedEmailId ?? '', index],
    queryFn: () => fetchNudgePreview(trackedEmailId as string, index),
    enabled: enabled && Boolean(trackedEmailId),
    // Always recompute on open — the whole point is "as of now".
    staleTime: 0,
    gcTime: 0,
    // 404/409 are terminal states (entry fired/cancelled), not transient.
    retry: (failureCount, err) =>
      !(err instanceof ApiClientError && (err.status === 404 || err.status === 409)) &&
      failureCount < 2,
  });
}
